<template>
  <div v-bind="$attrs" :class="['ews-radio ews-radio-outer', outerClass, $attrs?.class || ''].join(' ')">
    <label v-if="label" :class="labelClass">{{ label }}</label>
    <div :class="['ews-radio-wrapper', wrapperClass].join(' ')">
      <!-- Multiple radio options -->
      <div v-if="normalizedOptions && normalizedOptions.length > 0" :class="['ews-radio-element', elementClass].join(' ')">
        <div v-for="option in normalizedOptions" :key="option.value" class="radio-option">
          <slot name="antDesignSlot" :option="option" :antDesignProps="antDesignProps">
            <form-antd-radio
              v-bind="antDesignProps || {}"
              :disabled="option.disabled"
              :value="option.value"
              :modelValue="modelValue === option.value"
              @change="selectOption(option.value)"
            />
          </slot>
          <label :for="option.id" class="radio-label">{{ option.label }}</label>
        </div>
      </div>
      <!-- Single radio -->
      <div v-else :class="['ews-radio-element', elementClass].join(' ')">
        <slot name="antDesignSlot" :antDesignProps="antDesignProps">
          <form-antd-radio v-bind="antDesignProps || {}" :modelValue="modelValue" @update:value="(newValue) => $emit('update:modelValue', newValue)" />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import FormAntdRadio from './AntDesign/FormAntdRadio.vue'

export default {
  name: 'FormRadio',
  inheritAttrs: false,
  components: {
    FormAntdRadio
  },
  props: {
    /**
     * Type of box can either be 'checkbox' or 'radio'.
     */
    type: {
      type: String,
      default: 'radio'
    },
    /**
     * Optional label element above text input.
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Default (current) value.
     * @model
     */
    modelValue: [String, Number, Object, Array, Boolean],
    /**
     * This can be an array of values, array of objects or an object.
     */
    options: [Object, Array],
    /**
     * Additional classes for the wrapper.
     */
    wrapperClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the outer.
     */
    outerClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the inner.
     */
    elementClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the input.
     */
    inputClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for the label.
     */
    labelClass: {
      type: String,
      default: ''
    },
    /**
     * Additional classes for help text.
     */
    helpClass: {
      type: String,
      default: 'text-cool-gray-500 font-light text-sm ml-1'
    },
    /**
     * Additional classes for error text.
     */
    errorClass: {
      type: String,
      default: 'text-red-700 font-light text-sm ml-1'
    },
    /**
     * Ant Design Props.
     */
    antDesignProps: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  emits: ['update:modelValue'],
  computed: {
    normalizedOptions() {
      if (!this.options) return []

      if (Array.isArray(this.options)) {
        return this.options.map((item, i) => {
          if (typeof item === 'string' || typeof item === 'number') {
            return {
              value: item.toString(),
              label: item.toString(),
              id: `radio-${item.toString().trim()}-${i}`,
              disabled: false
            }
          }
          return {
            ...item,
            id: item.id || `radio-${item.value?.toString().trim()}-${i}`,
            disabled: item.disabled || false
          }
        })
      }

      if (typeof this.options === 'object') {
        return Object.entries(this.options).map(([key, value], i) => ({
          value: key,
          label: value,
          id: `radio-${key}-${i}`,
          disabled: false
        }))
      }

      return []
    }
  },
  methods: {
    selectOption(value) {
      this.$emit('update:modelValue', value)
    }
  }
}
</script>

<style lang="postcss" scoped>
.ews-radio-outer :deep(.ews-radio-element) {
  @apply mr-1;
}
.ews-radio-outer :deep(.ews-radio-wrapper) {
  @apply flex items-center;
}
.ews-radio-outer :deep(.radio-option) {
  @apply flex items-center mb-2;
}
.ews-radio-outer :deep(.radio-label) {
  @apply ml-2 font-light cursor-pointer;
}
.ews-radio-outer :deep(.ant-radio-wrapper:after) {
  display: none;
}
</style>
